import React from 'react';
import { useNode } from '@craftjs/core';
import { isBlankHtml } from '@/utils/functions';
import { iconMap } from './Icon';
import { htmlSeguro } from '../utils/htmlSeguro';

interface IconTextProps {
  icon?: string;
  iconSize?: number;
  iconColor?: string;
  iconPosition?: 'top' | 'left';
  title?: string;
  text?: string;
  titleColor?: string;
  textColor?: string;
  align?: 'left' | 'center';
  gap?: number;
  padding?: number;
}

/**
 * Ícono + texto — render del sitio público.
 *
 * El átomo de las grillas de beneficios: un ícono, un título y una descripción.
 * Metiendo varios en una `Grid` se arma lo mismo que hace `WhyChooseUs`, pero
 * con el contenido y el layout en manos del dealer.
 *
 * Usa el mismo `iconMap` que el componente `Icon`, así los nombres guardados
 * valen para los dos y no hay un segundo catálogo que mantener.
 *
 * Espejo de goautos-admin/src/components/builder2/userComponents/IconText.tsx.
 */
export const IconText = ({
  icon = 'star',
  iconSize = 32,
  iconColor = '#1e88e5',
  iconPosition = 'top',
  title = '',
  text = '',
  titleColor = '#111827',
  textColor = '#4b5563',
  align = 'left',
  gap = 12,
  padding = 0,
}: IconTextProps) => {
  const { connectors } = useNode();

  const Ico = iconMap[icon] || iconMap.star;
  const tamano = Math.max(12, Number(iconSize) || 32);
  const arriba = iconPosition === 'top';

  return (
    <div
      ref={(el: HTMLDivElement | null) => { if (el) connectors.connect(el); }}
      className={`flex w-full ${arriba ? 'flex-col' : 'flex-row'}`}
      style={{
        gap: `${gap}px`,
        padding: `${padding}px`,
        alignItems: arriba ? (align === 'center' ? 'center' : 'flex-start') : 'flex-start',
        textAlign: align,
        // Igual que en `Section`: el editor lo necesita para anclar sus controles
        // y acá no cambia nada, pero mantiene idéntico el estilo computado.
        position: 'relative',
      }}
    >
      <div style={{ flexShrink: 0, lineHeight: 0 }}>
        <Ico size={tamano} color={iconColor} />
      </div>
      <div style={{ minWidth: 0, flex: arriba ? undefined : '1 1 auto' }}>
        {!isBlankHtml(title) && (
          <h3
            className="mb-1 text-lg font-semibold"
            style={{ color: titleColor }}
            dangerouslySetInnerHTML={htmlSeguro(title || '')}
          />
        )}
        {!isBlankHtml(text) && (
          <div
            className="text-sm leading-relaxed"
            style={{ color: textColor }}
            dangerouslySetInnerHTML={htmlSeguro(text || '')}
          />
        )}
      </div>
    </div>
  );
};

IconText.craft = {
  displayName: 'IconText',
  props: {
    icon: 'star',
    iconSize: 32,
    iconColor: '#1e88e5',
    iconPosition: 'top',
    title: 'Un beneficio destacado',
    text: 'Explicá en una línea por qué le conviene al cliente.',
    titleColor: '#111827',
    textColor: '#4b5563',
    align: 'left',
    gap: 12,
    padding: 0,
  },
};
